/**
 * Autorização do Console Administrativo PETBEA.
 * Autorização é baseada em RECURSO + AÇÃO (+ escopo) — nunca no nome do papel.
 */

/* ------------------------------------------------------------------ */
/* CATÁLOGO DINÂMICO (petbea_admin_recursos / _modulos / _escopos)     */
/* As constantes abaixo são apenas PLANO B quando a leitura falha.    */
/* ------------------------------------------------------------------ */

export interface RecursoCatalogo {
  recurso: string;
  rotulo?: string | null;
  modulo_codigo?: string | null;
  escopo_codigo?: string | null;
  ordem?: number | null;
  dados_pessoais?: boolean | null;
}

export interface ModuloCatalogo {
  codigo: string;
  rotulo: string;
  icone?: string | null;
  ordem?: number | null;
}

export interface EscopoCatalogo {
  codigo: string;
  rotulo: string;
  descricao?: string | null;
  ordem?: number | null;
}

interface EstadoCatalogo {
  recursos: RecursoCatalogo[];
  modulos: ModuloCatalogo[];
  escopos: EscopoCatalogo[];
}

const CATALOGO: EstadoCatalogo = { recursos: [], modulos: [], escopos: [] };

/** Registra o catálogo devolvido por `listar_papeis` (rótulos, módulos e escopos do banco). */
export function registrarCatalogo(dados: {
  recursos?: RecursoCatalogo[] | null;
  modulos?: ModuloCatalogo[] | null;
  escopos?: EscopoCatalogo[] | null;
}): void {
  if (dados.recursos?.length) CATALOGO.recursos = dados.recursos;
  if (dados.modulos?.length) CATALOGO.modulos = dados.modulos;
  if (dados.escopos?.length) CATALOGO.escopos = dados.escopos;
}

export function catalogoRecursos(): RecursoCatalogo[] {
  return CATALOGO.recursos;
}

export function catalogoModulos(): ModuloCatalogo[] {
  return CATALOGO.modulos;
}

export function catalogoEscopos(): EscopoCatalogo[] {
  return CATALOGO.escopos;
}

/** Escopos disponíveis: do banco quando já carregado; senão, o plano B local. */
export function escoposDisponiveis(): EscopoCatalogo[] {
  if (CATALOGO.escopos.length > 0) return CATALOGO.escopos;
  return ESCOPOS_ADMIN.map((e, i) => ({ codigo: e.codigo, rotulo: e.label, ordem: i + 1 }));
}

/** Módulo ao qual o recurso pertence (do catálogo do banco). */
export function moduloDoRecurso(recurso: string): ModuloCatalogo | null {
  const rec = CATALOGO.recursos.find((r) => r.recurso === recurso);
  if (!rec?.modulo_codigo) return null;
  return CATALOGO.modulos.find((m) => m.codigo === rec.modulo_codigo) ?? null;
}

/** Escopo do recurso (vem de petbea_admin_recursos). */
export function escopoDoRecurso(recurso: string): EscopoCatalogo | null {
  const rec = CATALOGO.recursos.find((r) => r.recurso === recurso);
  if (!rec?.escopo_codigo) return null;
  return (
    CATALOGO.escopos.find((e) => e.codigo === rec.escopo_codigo) ?? {
      codigo: rec.escopo_codigo,
      rotulo: rec.escopo_codigo,
    }
  );
}

/** true quando o recurso trata dados pessoais (LGPD) — mostra o cadeado. */
export function recursoComDadosPessoais(recurso: string): boolean {
  return CATALOGO.recursos.find((r) => r.recurso === recurso)?.dados_pessoais === true;
}

/** Rótulo do recurso no catálogo do banco. */
export function rotuloRecursoCatalogo(recurso: string): string {
  return CATALOGO.recursos.find((r) => r.recurso === recurso)?.rotulo ?? rotuloRecurso(recurso);
}

export const ROTULO_RECURSO: Record<string, string> = {
  ANIMAL: 'Animais',
  ANIMAL_VINCULO: 'Vínculos de Animais',
  CADERNETA: 'Caderneta Digital',
  ADOCAO: 'Adoção',
  USUARIOS: 'Usuários e Acessos',
  ORGANIZACAO: 'Organizações',
  ACOMPANHAMENTO: 'Acompanhamentos',
  CAMPANHA: 'Campanhas',
  UPAVET: 'UPA VET',
  UPAVET_ATENDIMENTOS: 'UPA VET · Atendimentos',
  UPAVET_PROCEDIMENTOS: 'UPA VET · Procedimentos',
  UPAVET_ANIMAIS: 'UPA VET · Animais atendidos',
  UPAVET_CASTROMETRO: 'UPA VET · Castrômetro',
  UPAVET_CONSOLIDACAO: 'UPA VET · Consolidação',
  UPAVET_AUDITORIA: 'UPA VET · Auditoria da prestação',
  UPAVET_INDICADORES: 'UPA VET · Indicadores & qualidade',
  UPAVET_EXECUTIVO: 'UPA VET · Relatório executivo',
  UPAVET_CUSTOS: 'UPA VET · Custos & benchmark',
  UPAVET_CATALOGO: 'UPA VET · Catálogo de itens',
  UPAVET_TUTORES_UNICOS: 'UPA VET · Tutores únicos',
  UPAVET_ANIMAIS_UNICOS: 'UPA VET · Animais únicos',
  UPAVET_TUTOR_ANIMAIS: 'UPA VET · Tutor + animais',
  UPAVET_CONCILIACAO: 'UPA VET · Conciliação',
  UPAVET_DIAGNOSTICO: 'UPA VET · Diagnóstico da API',
  UPAVET_FORA_GOIANIA: 'UPA VET · Auditoria de Atendimentos',
  AUDITORIA: 'Auditoria',
  DOCUMENTOS: 'Documentos da parceria',
  RELATORIOS: 'Relatórios',
  CONFIGURACOES: 'Configurações',
};

export const ROTULO_ACAO: Record<string, string> = {
  visualizar: 'Visualizar',
  criar: 'Criar',
  editar: 'Editar',
  excluir: 'Excluir',
  validar: 'Validar',
  inativar: 'Inativar',
  exportar: 'Exportar',
  registrar: 'Registrar',
  revogar: 'Revogar',
  aprovar: 'Aprovar',
  importar: 'Importar',
  alterar_permissoes: 'Alterar permissões',
  bloquear: 'Bloquear',
  encerrar: 'Encerrar',
  reabrir: 'Reabrir',
  atender: 'Atender',
  substituir_responsavel: 'Substituir responsável',
};

export const ROTULO_PAPEL: Record<string, string> = {
  SUPER_ADMIN: 'Super Administrador',
  ADMIN_AMMA: 'Administrador AMMA',
  GESTOR_BEM_ESTAR: 'Gestor de Bem-Estar Animal',
  GESTOR_UPAVET: 'Gestor UPA VET',
  VETERINARIO: 'Veterinário',
  RECEPCAO_UPAVET: 'Recepção UPA VET',
  OPERADOR_CADASTRO: 'Operador de Cadastro',
  GESTOR_ADOCAO: 'Gestor de Adoção',
  GESTOR_CAMPANHAS: 'Gestor de Campanhas',
  ANALISTA_DADOS: 'Analista de Dados',
  AUDITOR: 'Auditor',
  CONSULTA: 'Consulta',
};

export const ROTULO_STATUS_OPERADOR: Record<string, string> = {
  ATIVO: 'Ativo',
  CONVIDADO: 'Convidado',
  SUSPENSO: 'Suspenso',
  BLOQUEADO: 'Bloqueado',
};

/**
 * Plano B dos escopos de atuação — a fonte oficial é petbea_admin_escopos
 * (lida por `listar_papeis` e registrada via `registrarCatalogo`).
 */
export const ESCOPOS_ADMIN = [
  { codigo: 'BEM_ESTAR', label: 'Bem-Estar Animal' },
  { codigo: 'UPAVET', label: 'UPA VET' },
  { codigo: 'ADOCAO', label: 'Adoção' },
  { codigo: 'CAMPANHAS', label: 'Campanhas' },
  { codigo: 'AMMA', label: 'Gestão AMMA' },
];

export function rotuloRecurso(recurso: string): string {
  const doBanco = CATALOGO.recursos.find((r) => r.recurso === recurso)?.rotulo;
  return doBanco ?? ROTULO_RECURSO[recurso] ?? recurso;
}

export function rotuloAcao(acao: string): string {
  return ROTULO_ACAO[acao] ?? acao;
}

export function rotuloPapel(codigo: string): string {
  return ROTULO_PAPEL[codigo] ?? codigo;
}

export function rotuloEscopo(codigo: string): string {
  return (
    CATALOGO.escopos.find((e) => e.codigo === codigo)?.rotulo ??
    ESCOPOS_ADMIN.find((e) => e.codigo === codigo)?.label ??
    codigo
  );
}

/** Descrição do escopo (usada nos checkboxes de "Somente estes"). */
export function descricaoEscopo(codigo: string): string {
  return CATALOGO.escopos.find((e) => e.codigo === codigo)?.descricao ?? '';
}

/** Verifica a permissão RECURSO:AÇÃO. */
export function temPermissao(permissoes: string[], recurso: string, acao: string): boolean {
  return permissoes.includes(`${recurso}:${acao}`);
}

/**
 * PLANO B da ordem dos recursos do UPA VET.
 * A ordem oficial vem de petbea_admin_modulos + petbea_admin_recursos.
 */
export const ORDEM_RECURSOS_UPAVET: string[] = [
  'UPAVET_ATENDIMENTOS',
  'UPAVET_PROCEDIMENTOS',
  'UPAVET_ANIMAIS',
  'UPAVET_CASTROMETRO',
  'UPAVET_CONSOLIDACAO',
  'UPAVET_AUDITORIA',
  'UPAVET_INDICADORES',
  'UPAVET_EXECUTIVO',
  'UPAVET_CUSTOS',
  'UPAVET_CATALOGO',
  'UPAVET_TUTORES_UNICOS',
  'UPAVET_ANIMAIS_UNICOS',
  'UPAVET_TUTOR_ANIMAIS',
  'UPAVET_CONCILIACAO',
  'UPAVET_DIAGNOSTICO',
  'UPAVET_FORA_GOIANIA',
  'UPAVET',
];

/** Ordem canônica das colunas de ação na matriz. */
export const ORDEM_ACOES: string[] = [
  'visualizar',
  'criar',
  'editar',
  'validar',
  'importar',
  'exportar',
  'aprovar',
  'excluir',
  'inativar',
  'registrar',
  'revogar',
  'bloquear',
  'encerrar',
  'reabrir',
  'atender',
  'substituir_responsavel',
  'alterar_permissoes',
];

/** Ordem do módulo (do catálogo) — módulos sem cadastro vão para o fim. */
export function ordemDoModulo(codigo: string | null | undefined): number {
  if (!codigo) return Number.MAX_SAFE_INTEGER;
  const m = CATALOGO.modulos.find((x) => x.codigo === codigo);
  return m?.ordem ?? Number.MAX_SAFE_INTEGER;
}

/** Ordem do recurso dentro do módulo (do catálogo). */
export function ordemDoRecurso(recurso: string): number {
  const r = CATALOGO.recursos.find((x) => x.recurso === recurso);
  if (r?.ordem !== null && r?.ordem !== undefined) return r.ordem;
  const i = ORDEM_RECURSOS_UPAVET.indexOf(recurso);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

/**
 * Ordena recursos: primeiro pela ordem do MÓDULO (petbea_admin_modulos) e,
 * dentro dele, pela ordem do recurso (petbea_admin_recursos). Quando o catálogo
 * ainda não foi lido, cai para o plano B (UPA VET primeiro).
 */
export function ordenarRecursos(recursos: string[]): string[] {
  const unicos = Array.from(new Set(recursos));
  if (CATALOGO.recursos.length === 0) {
    const indiceUpavet = (r: string) => {
      const i = ORDEM_RECURSOS_UPAVET.indexOf(r);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    const upavet = unicos
      .filter((r) => ORDEM_RECURSOS_UPAVET.includes(r))
      .sort((a, b) => indiceUpavet(a) - indiceUpavet(b));
    const demais = unicos
      .filter((r) => !ORDEM_RECURSOS_UPAVET.includes(r))
      .sort((a, b) => rotuloRecurso(a).localeCompare(rotuloRecurso(b), 'pt-BR'));
    return [...upavet, ...demais];
  }
  return [...unicos].sort((a, b) => {
    const ma = ordemDoModulo(CATALOGO.recursos.find((r) => r.recurso === a)?.modulo_codigo);
    const mb = ordemDoModulo(CATALOGO.recursos.find((r) => r.recurso === b)?.modulo_codigo);
    if (ma !== mb) return ma - mb;
    const ra = ordemDoRecurso(a);
    const rb = ordemDoRecurso(b);
    if (ra !== rb) return ra - rb;
    return rotuloRecurso(a).localeCompare(rotuloRecurso(b), 'pt-BR');
  });
}

/** Ordena as ações pela ordem canônica; ações desconhecidas vão para o fim. */
export function ordenarAcoes(acoes: string[]): string[] {
  const unicas = Array.from(new Set(acoes));
  return unicas.sort((a, b) => {
    const ia = ORDEM_ACOES.indexOf(a);
    const ib = ORDEM_ACOES.indexOf(b);
    const va = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
    const vb = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
    return va === vb ? a.localeCompare(b, 'pt-BR') : va - vb;
  });
}

/** Agrupa as permissões por recurso para exibição. */
export function agruparPermissoes(permissoes: string[]): Record<string, string[]> {
  return permissoes.reduce<Record<string, string[]>>((acc, item) => {
    const [recurso, acao] = item.split(':');
    if (!recurso || !acao) return acc;
    acc[recurso] = acc[recurso] ?? [];
    acc[recurso].push(acao);
    return acc;
  }, {});
}

/* ------------------------------------------------------------------ */
/* LGPD — mascaramento de dados sensíveis (spec, seção 9)             */
/* ------------------------------------------------------------------ */

export function mascararEmail(email: string | null | undefined): string {
  if (!email) return '—';
  const [nome, dominio] = email.split('@');
  if (!dominio) return '•••••';
  const visivel = nome.slice(0, 2);
  return `${visivel}${'•'.repeat(Math.max(nome.length - 2, 3))}@${dominio}`;
}

export function mascararCpf(cpf: string | null | undefined): string {
  if (!cpf) return '—';
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11) return '•••.•••.•••-••';
  return `•••.${d.slice(3, 6)}.•••-${d.slice(9, 11)}`;
}

export function mascararTelefone(telefone: string | null | undefined): string {
  if (!telefone) return '—';
  const d = telefone.replace(/\D/g, '');
  if (d.length < 4) return '•••••';
  return `(••) •••••-${d.slice(-2).padStart(4, '•')}`;
}